// Terms of Service - Conditions Générales d'Utilisation
import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { FileText, AlertTriangle, CheckCircle, Ban, Scale, Clock } from 'lucide-react';

const Terms = () => {
  const { isDarkMode } = useTheme();

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-2xl mb-4">
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Conditions Générales d'Utilisation
          </h1>
          <p className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            Dernière mise à jour : Mars 2026
          </p>
        </div>

        {/* Content */}
        <div className={`prose max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
          
          {/* Article 1 */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <Scale className="w-5 h-5 text-blue-500" />
              Article 1 - Objet
            </h2>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de la plateforme drbooking, 
              service de mise en relation entre patients et professionnels de santé pour la prise de rendez-vous médicaux.
            </p>
          </section>

          {/* Article 2 */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <CheckCircle className="w-5 h-5 text-green-500" />
              Article 2 - Acceptation des CGU
            </h2>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              L'utilisation de drbooking implique l'acceptation pleine et entière des présentes CGU. 
              En créant un compte, vous reconnaissez avoir lu, compris et accepté ces conditions.
            </p>
          </section>

          {/* Article 3 */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <FileText className="w-5 h-5 text-blue-500" />
              Article 3 - Services proposés
            </h2>
            <div className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              <p className="mb-3">drbooking propose les services suivants :</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Recherche de professionnels de santé par spécialité et localisation</li>
                <li>Prise de rendez-vous en ligne</li>
                <li>Téléconsultation vidéo</li>
                <li>Rappels automatiques de rendez-vous</li>
                <li>Liste d'attente intelligente</li>
                <li>Gestion de cabinet pour les professionnels</li>
              </ul>
            </div>
          </section>

          {/* Article 4 */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <Clock className="w-5 h-5 text-orange-500" />
              Article 4 - Tarification
            </h2>
            <div className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              <p className="mb-3"><strong>Pour les patients :</strong></p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Inscription : Gratuite</li>
                <li>Accès de base : 1€ (paiement unique à vie)</li>
                <li>Abonnement Premium : 5€/mois (liste d'attente prioritaire)</li>
              </ul>
              <p className="mb-3"><strong>Pour les professionnels :</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>Abonnement mensuel : 150€/mois</li>
              </ul>
            </div>
          </section>

          {/* Article 5 */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <Ban className="w-5 h-5 text-red-500" />
              Article 5 - Obligations des utilisateurs
            </h2>
            <div className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              <p className="mb-3">L'utilisateur s'engage à :</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Fournir des informations exactes et à jour</li>
                <li>Ne pas usurper l'identité d'un tiers</li>
                <li>Respecter les rendez-vous pris ou les annuler dans les délais impartis (24h minimum)</li>
                <li>Ne pas utiliser la plateforme à des fins illicites</li>
                <li>Maintenir la confidentialité de ses identifiants de connexion</li>
              </ul>
            </div>
          </section>

          {/* Article 6 */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Article 6 - Limitation de responsabilité
            </h2>
            <div className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              <p className="mb-3">
                drbooking est un service de mise en relation et ne se substitue pas à une consultation médicale. 
                drbooking ne peut être tenu responsable :
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>De la qualité des soins prodigués par les professionnels</li>
                <li>Des annulations ou retards de rendez-vous par les professionnels</li>
                <li>Des interruptions temporaires du service pour maintenance</li>
                <li>De l'utilisation frauduleuse de vos identifiants</li>
              </ul>
            </div>
          </section>

          {/* Article 7 */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Article 7 - Résiliation
            </h2>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              L'utilisateur peut supprimer son compte à tout moment depuis les paramètres de son profil. 
              drbooking se réserve le droit de suspendre ou supprimer un compte en cas de violation des présentes CGU.
            </p>
          </section>

          {/* Article 8 - Âge minimum */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Article 8 - Âge minimum et capacité juridique
            </h2>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              L'utilisation de drbooking est réservée aux personnes majeures (18 ans et plus) ou aux mineurs 
              disposant d'une autorisation parentale. En créant un compte, vous déclarez avoir la capacité juridique 
              de conclure un contrat ou agir sous la responsabilité d'un représentant légal.
            </p>
          </section>

          {/* Article 9 */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Article 9 - Droit applicable
            </h2>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              Les présentes CGU sont régies par le droit français. 
              Tout litige relatif à leur interprétation et/ou exécution relève des tribunaux français compétents.
            </p>
          </section>

          {/* Mentions légales éditeur */}
          <section className={`p-6 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Mentions légales de l'éditeur
            </h2>
            <div className={`space-y-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              <p><strong>Éditeur :</strong> drbooking S.à r.l.</p>
              <p><strong>Siège social :</strong> Luxembourg</p>
              <p><strong>Email de contact :</strong> contact@drbooking.pro</p>
              <p><strong>WhatsApp :</strong> +352 621 411 475</p>
              <p><strong>Hébergeur :</strong> Emergent Labs - Cloud Infrastructure</p>
            </div>
          </section>

          {/* Contact */}
          <section className={`p-6 rounded-xl text-center ${isDarkMode ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Questions sur les CGU ?
            </h3>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>
              Contactez-nous : <a href="mailto:contact@drbooking.pro" className="text-blue-500 hover:underline">contact@drbooking.pro</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;
